import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './integrations.css';

/**
 * Modal d'analyse de ticket JIRA avec LLM
 * Affiche les nodes/tests suggérés avec score de confiance
 */
const JiraAnalyzerModal = ({ isOpen, onClose, onNodesGenerated }) => {
  const { t } = useTranslation();
  const [ticketKey, setTicketKey] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  
  // 🆕 État pour la liste des tickets
  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showTicketList, setShowTicketList] = useState(true);

  // 🆕 État pour les filtres
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // 'all', 'me', 'unassigned'

  // 🆕 État pour la déconnexion
  const [disconnecting, setDisconnecting] = useState(false);

  if (!isOpen) return null;

  // 🆕 Charger les projets au montage
  useEffect(() => {
    if (isOpen) {
      fetchProjects();
      fetchTickets();
    }
  }, [isOpen]);

  // 🆕 Recharger les tickets quand les filtres changent
  useEffect(() => {
    if (isOpen && (selectedProject || assigneeFilter !== 'all')) {
      fetchTickets();
    }
  }, [selectedProject, assigneeFilter]);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/integration/jira/projects', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (err) {
      console.error('Erreur fetchProjects:', err);
    }
  };

  const fetchTickets = async () => {
    setLoadingTickets(true);
    try {
      // Construire le JQL en fonction des filtres
      let jql = '';
      const conditions = [];

      if (selectedProject) {
        conditions.push(`project = ${selectedProject}`);
      }

      if (assigneeFilter === 'me') {
        conditions.push('assignee = currentUser()');
      } else if (assigneeFilter === 'unassigned') {
        conditions.push('assignee is EMPTY');
      }

      if (conditions.length > 0) {
        jql = conditions.join(' AND ') + ' ORDER BY updated DESC';
      }

      const url = jql 
        ? `/api/integration/jira/tickets?jql=${encodeURIComponent(jql)}&maxResults=20`
        : '/api/integration/jira/tickets?maxResults=20';

      const response = await fetch(url, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(t('jiraIntegration.analyzer.fetchError'));
      }

      const data = await response.json();
      setTickets(data.issues);
    } catch (err) {
      console.error('Erreur fetchTickets:', err);
      setError(err.message);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleAnalyze = async (explicitKey = null) => {
    const keyToAnalyze = explicitKey || ticketKey;
    
    if (!keyToAnalyze.match(/^[A-Z]{2,}-\d+$/)) {
      setError(t('jiraIntegration.analyzer.manual.invalidFormat'));
      return;
    }

    setAnalyzing(true);
    setError(null);
    setAnalysis(null);

    try {
      const response = await fetch('/api/integration/jira/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ticketKey: keyToAnalyze, includeLinkedPRs: false })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('jiraIntegration.analyzer.analysisError'));
      }

      setAnalysis(data);
      // Sélectionner par défaut les nodes avec confidence > 0.7
      setSelectedNodes(
        data.suggestedNodes
          .filter(n => n.confidence >= 0.7)
          .map(n => n.name)
      );
      setSelectedTests(
        data.suggestedTests
          .filter(t => t.confidence >= 0.7)
          .map(t => t.scenario)
      );
    } catch (err) {
      console.error('Erreur analyse JIRA:', err);
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleNodeToggle = (nodeName) => {
    setSelectedNodes(prev =>
      prev.includes(nodeName)
        ? prev.filter(n => n !== nodeName)
        : [...prev, nodeName]
    );
  };

  const handleTestToggle = (scenario) => {
    setSelectedTests(prev =>
      prev.includes(scenario)
        ? prev.filter(s => s !== scenario)
        : [...prev, scenario]
    );
  };

  const handleAddToModeling = () => {
    if (selectedNodes.length === 0) {
      setError(t('jiraIntegration.results.selectAtLeastOne'));
      return;
    }

    const nodesToAdd = analysis.suggestedNodes
      .filter(n => selectedNodes.includes(n.name))
      .map(n => ({
        name: n.name,
        type: n.type,
        properties: n.properties,
        source: 'JIRA',
        jiraTicket: analysis.jiraTicket
      }));

    const testsToAdd = analysis.suggestedTests
      .filter(t => selectedTests.includes(t.scenario))
      .map(t => ({
        scenario: t.scenario,
        given: t.given,
        when: t.when,
        then: t.then,
        source: 'JIRA',
        jiraTicket: analysis.jiraTicket
      }));

    onNodesGenerated && onNodesGenerated({
      nodes: nodesToAdd,
      tests: testsToAdd,
      relations: analysis.suggestedRelations
    });

    onClose();
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return '#4caf50'; // Vert
    if (confidence >= 0.6) return '#ff9800'; // Orange
    return '#f44336'; // Rouge
  };

  // 🆕 Fonction de déconnexion JIRA
  const handleLogout = async () => {
    if (!window.confirm(t('jiraIntegration.logout.confirmMessage'))) {
      return;
    }

    setDisconnecting(true);
    setError(null);

    try {
      const response = await fetch('/api/integration/jira/logout', {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || t('jiraIntegration.logout.error'));
      }

      alert(t('jiraIntegration.logout.success'));
      onClose(); // Fermer la modal après déconnexion
      window.location.reload(); // Rafraîchir pour mettre à jour l'état global
    } catch (err) {
      console.error('Erreur logout JIRA:', err);
      setError(err.message || t('jiraIntegration.logout.error'));
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="integration-modal-overlay" onClick={onClose}>
      <div className="integration-modal-content integration-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="integration-modal-header">
          <h2>{t('jiraIntegration.analyzer.title')}</h2>
          <div className="integration-modal-header-actions">
            <button 
              className="integration-button-small integration-button-danger"
              onClick={handleLogout}
              disabled={disconnecting}
              title={t('jiraIntegration.logout.disconnect')}
            >
              {disconnecting ? '⏳' : '🗑️'} {t('jiraIntegration.logout.disconnect')}
            </button>
            <button className="integration-modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Onglets : Liste des tickets / Saisie manuelle */}
        <div className="integration-tabs">
          <button
            className={`integration-tab ${showTicketList ? 'active' : ''}`}
            onClick={() => {
              setShowTicketList(true);
              setError(null); // Clear error when switching tabs
            }}
          >
            {t('jiraIntegration.analyzer.tabs.list')}
          </button>
          <button
            className={`integration-tab ${!showTicketList ? 'active' : ''}`}
            onClick={() => {
              setShowTicketList(false);
              setError(null); // Clear error when switching tabs
            }}
          >
            {t('jiraIntegration.analyzer.tabs.manual')}
          </button>
        </div>

        {/* Filtres pour la liste des tickets */}
        {showTicketList && (
          <div className="integration-filters">
            <div className="integration-filter-group">
              <label>{t('jiraIntegration.analyzer.filters.project')}</label>
              <select 
                value={selectedProject} 
                onChange={(e) => setSelectedProject(e.target.value)}
                className="integration-filter-select"
              >
                <option value="">{t('jiraIntegration.analyzer.filters.allProjects')}</option>
                {projects.map(project => (
                  <option key={project.key} value={project.key}>
                    {project.name} ({project.key})
                  </option>
                ))}
              </select>
            </div>

            <div className="integration-filter-group">
              <label>{t('jiraIntegration.analyzer.filters.assignee')}</label>
              <select 
                value={assigneeFilter} 
                onChange={(e) => setAssigneeFilter(e.target.value)}
                className="integration-filter-select"
              >
                <option value="all">{t('jiraIntegration.analyzer.filters.allAssignees')}</option>
                <option value="me">{t('jiraIntegration.analyzer.filters.myTickets')}</option>
                <option value="unassigned">{t('jiraIntegration.analyzer.filters.unassigned')}</option>
              </select>
            </div>
          </div>
        )}

        {/* Liste des tickets */}
        {showTicketList && (
          <div className="integration-ticket-list">
            {loadingTickets ? (
              <div className="integration-loading">
                <div className="integration-spinner"></div>
                <p>{t('jiraIntegration.analyzer.loading')}</p>
              </div>
            ) : tickets.length > 0 ? (
              <div className="integration-tickets-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('jiraIntegration.analyzer.table.key')}</th>
                      <th>{t('jiraIntegration.analyzer.table.summary')}</th>
                      <th>{t('jiraIntegration.analyzer.table.type')}</th>
                      <th>{t('jiraIntegration.analyzer.table.status')}</th>
                      <th>{t('jiraIntegration.analyzer.table.assignee')}</th>
                      <th>{t('jiraIntegration.analyzer.table.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr key={ticket.key}>
                        <td><strong>{ticket.key}</strong></td>
                        <td>
                          <div className="integration-ticket-summary">
                            {ticket.summary}
                            {ticket.description && typeof ticket.description === 'string' && (
                              <small>
                                {ticket.description.substring(0, 80)}
                                {ticket.description.length > 80 ? '...' : ''}
                              </small>
                            )}
                          </div>
                        </td>
                        <td><span className="integration-badge">{ticket.issueType}</span></td>
                        <td><span className="integration-badge integration-badge-status">{ticket.status}</span></td>
                        <td>{ticket.assignee}</td>
                        <td>
                          <button
                            onClick={() => {
                              const key = ticket.key;
                              setShowTicketList(false);
                              setError(null);
                              setTicketKey(key); // Update state for display
                              handleAnalyze(key); // Pass key directly
                            }}
                            className="integration-button-small"
                          >
                            {t('jiraIntegration.analyzer.table.analyze')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="integration-no-tickets">
                <p>{t('jiraIntegration.analyzer.noTickets')}</p>
                <button onClick={fetchTickets} className="integration-button integration-button-secondary">
                  {t('jiraIntegration.analyzer.filters.refresh')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Saisie manuelle */}
        {!showTicketList && (
          <div className="integration-jira-input">
            <input
              type="text"
              value={ticketKey}
              onChange={(e) => setTicketKey(e.target.value.toUpperCase())}
              placeholder={t('jiraIntegration.analyzer.manual.ticketKeyPlaceholder')}
              className="integration-input"
              disabled={analyzing}
            />
            <button
              onClick={handleAnalyze}
              className="integration-button integration-button-primary"
              disabled={analyzing || !ticketKey}
            >
              {analyzing ? t('jiraIntegration.analyzer.analyzing') : t('jiraIntegration.analyzer.manual.analyze')}
            </button>
          </div>
        )}

        {error && (
          <div className="integration-error-message">
            <span>⚠️</span> {error}
          </div>
        )}

        {analyzing && (
          <div className="integration-loading">
            <div className="integration-spinner"></div>
            <p>{t('jiraIntegration.analyzer.analyzing')}</p>
          </div>
        )}

        {analysis && (
          <div className="integration-analysis-results">
            {/* Résumé */}
            <div className="integration-summary">
              <h3>{t('jiraIntegration.results.summary')}</h3>
              <p>{analysis.summary}</p>
              <a href={analysis.jiraTicket.url} target="_blank" rel="noopener noreferrer">
                {t('jiraIntegration.results.viewTicket')}
              </a>
            </div>

            {/* Nodes suggérés */}
            <div className="integration-section">
              <h3>{t('jiraIntegration.results.suggestedNodes', { count: analysis.suggestedNodes.length })}</h3>
              <div className="integration-nodes-list">
                {analysis.suggestedNodes.map((node, idx) => (
                  <div key={idx} className="integration-suggested-node">
                    <label className="integration-node-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedNodes.includes(node.name)}
                        onChange={() => handleNodeToggle(node.name)}
                      />
                      <div className="integration-node-details">
                        <div className="integration-node-header">
                          <strong>{node.name}</strong>
                          <span className="integration-node-type">{node.type}</span>
                          <span
                            className="integration-confidence-badge"
                            style={{ backgroundColor: getConfidenceColor(node.confidence) }}
                          >
                            {Math.round(node.confidence * 100)}%
                          </span>
                        </div>
                        <p className="integration-node-description">
                          {node.properties.description}
                        </p>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Relations suggérées */}
            {analysis.suggestedRelations.length > 0 && (
              <div className="integration-section">
                <h3>{t('jiraIntegration.results.suggestedRelations', { count: analysis.suggestedRelations.length })}</h3>
                <ul className="integration-relations-list">
                  {analysis.suggestedRelations.map((rel, idx) => (
                    <li key={idx}>
                      <strong>{rel.from}</strong> → <code>{rel.type}</code> → <strong>{rel.to}</strong>
                      <span
                        className="integration-confidence-badge"
                        style={{ backgroundColor: getConfidenceColor(rel.confidence) }}
                      >
                        {Math.round(rel.confidence * 100)}%
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tests suggérés */}
            {analysis.suggestedTests.length > 0 && (
              <div className="integration-section">
                <h3>{t('jiraIntegration.results.suggestedTests', { count: analysis.suggestedTests.length })}</h3>
                <div className="integration-tests-list">
                  {analysis.suggestedTests.map((test, idx) => (
                    <div key={idx} className="integration-suggested-test">
                      <label className="integration-test-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedTests.includes(test.scenario)}
                          onChange={() => handleTestToggle(test.scenario)}
                        />
                        <div className="integration-test-details">
                          <div className="integration-test-header">
                            <strong>{test.scenario}</strong>
                            <span
                              className="integration-confidence-badge"
                              style={{ backgroundColor: getConfidenceColor(test.confidence) }}
                            >
                              {Math.round(test.confidence * 100)}%
                            </span>
                          </div>
                          <div className="integration-gherkin">
                            <div><strong>Given:</strong> {test.given}</div>
                            <div><strong>When:</strong> {test.when}</div>
                            <div><strong>Then:</strong> {test.then}</div>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="integration-modal-actions">
              <button
                onClick={onClose}
                className="integration-button integration-button-secondary"
              >
                {t('jiraIntegration.results.cancel')}
              </button>
              <button
                onClick={handleAddToModeling}
                className="integration-button integration-button-primary"
                disabled={selectedNodes.length === 0}
              >
                {t('jiraIntegration.results.addToModeling')} ({selectedNodes.length} nodes, {selectedTests.length} tests)
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JiraAnalyzerModal;
